import { useFloating, useFloatingNodeId, offset, flip, shift, hide, autoUpdate, FloatingNode, FloatingPortal } from '@floating-ui/react';
import { Popover, Transition } from '@headlessui/react';
import { Children, createContext, Fragment, isValidElement, ReactNode, useContext } from 'react';

const styles = {
    body: {
        left: 'left-full transform -translate-x-full',
        center: 'left-1/2 transform -translate-x-1/2',
        right: '',
    },
};

export type MenuProps = {
    children: ReactNode | ReactNode[];
};

const FloatingContext = createContext(null);

export function Menu({ children }: MenuProps): JSX.Element {
    let _TriggerButton: ReactNode;
    let _MenuBody: ReactNode;

    const nodeId = useFloatingNodeId();

    const floating = useFloating({
        nodeId,
        placement: 'bottom',
        middleware: [flip(), shift(), offset(4), hide()],
        whileElementsMounted: autoUpdate,
    });

    Children.map(children, (child) => {
        if (isValidElement(child)) {
            if (child.type === TriggerButton) {
                _TriggerButton = child;
            }
            if (child.type === MenuBody) {
                _MenuBody = child;
            }
        }
    });

    return (
        <FloatingContext.Provider value={floating}>
            <FloatingNode id={nodeId}>
                <Popover className="relative">
                    {({ open }): ReactNode => (
                        <>
                            <div ref={floating.refs.setReference}>{_TriggerButton}</div>
                            {_MenuBody}
                        </>
                    )}
                </Popover>
            </FloatingNode>
        </FloatingContext.Provider>
    );
}

type TriggerButtonProps = {
    children: ReactNode;
    disabled?: boolean;
    className?: string;
    onClick?: () => void;
};

function TriggerButton({ children, className, disabled = false, onClick }: TriggerButtonProps): JSX.Element {
    return (
        <Popover.Button as={'button'} className={className} disabled={disabled} onClick={onClick}>
            {children}
        </Popover.Button>
    );
}

type MenuBodyProps = {
    position?: 'left' | 'center' | 'right';
    children: ReactNode;
    isOpen?: boolean;
};

function MenuBody({ children, position = 'right', isOpen }: MenuBodyProps): JSX.Element {
    const floating = useContext(FloatingContext);
    if (isOpen === false) {
        return <></>;
    }

    return (
        <FloatingPortal>
            <Popover.Panel static={isOpen !== undefined}>
                <div
                    ref={floating.refs.setFloating}
                    style={{ ...floating.floatingStyles, visibility: floating.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible' }}
                    className="z-[55]"
                >
                    <div
                        className={` rounded-md bg-white dark:border dark:border-white/[0.12] shadow-lg  dark:bg-[#333333] min-w-max px-0`}
                    >
                        <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 relative">{children}</div>
                    </div>
                </div>
            </Popover.Panel>
        </FloatingPortal>
    );
}

Menu.Button = TriggerButton;
Menu.Body = MenuBody;

export default Menu;
